import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HostedActualServer, fetchHostedServerTlsReady } from '../utils/hostingApi';

const TLS_READY_POLL_MS = 2000;
const TLS_READY_MAX_ATTEMPTS = 45;

export function useHostedServerTlsReady(server: HostedActualServer | null) {
  const queryClient = useQueryClient();
  const attemptsRef = useRef(0);
  const needsTlsCheck = server?.status === 'active' && Boolean(server.serverUrl);
  const queryKey = ['hostedServerTlsReady', server?.serverUrl, server?.updatedAt] as const;

  useEffect(() => {
    attemptsRef.current = 0;
  }, [server?.serverUrl, server?.updatedAt]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      attemptsRef.current += 1;
      return fetchHostedServerTlsReady();
    },
    enabled: needsTlsCheck,
    refetchInterval: (queryState) => {
      if (queryState.state.data === true) {
        return false;
      }
      if (attemptsRef.current >= TLS_READY_MAX_ATTEMPTS) {
        return false;
      }
      return TLS_READY_POLL_MS;
    },
    staleTime: Infinity
  });

  const tlsReady = !needsTlsCheck || query.data === true;
  const tlsCheckTimedOut =
    needsTlsCheck && !tlsReady && attemptsRef.current >= TLS_READY_MAX_ATTEMPTS;
  const isCheckingTls =
    needsTlsCheck &&
    !tlsReady &&
    !tlsCheckTimedOut &&
    (query.isLoading || query.isFetching || query.data === false);

  const recheckTls = () => {
    attemptsRef.current = 0;
    queryClient.resetQueries({ queryKey });
  };

  return {
    tlsReady,
    isCheckingTls,
    tlsCheckTimedOut,
    recheckTls
  };
}
